import { createTRPCClient, httpLink } from "@trpc/client";
import type { PortalRouter } from "../../../../server/src/index";
import superjson from "superjson";
import Cookies from "js-cookie";
import { API_URL } from "../constants/api.const";
import { COOKIE_KEYS } from "@/shared/constants/cookies.const";
import { notifyAuthSessionChanged } from "./authSessionEvents";
import {
  ensureAccessViaRefresh,
  isAccessTokenFresh,
  isTrpcUnauthorizedResponse,
  saveAccessToken,
} from "./portalRefresh";

// Get token from cookies
export const getToken = () => {
  return Cookies.get(COOKIE_KEYS.token);
};

// Save access token (Secure on HTTPS / prod)
export const saveToken = (token: string) => {
  saveAccessToken(token);
};

// Remove token (logout)
export const removeToken = () => {
  Cookies.remove(COOKIE_KEYS.token, { path: "/" });
  notifyAuthSessionChanged();
};

// Check if logged in
export const isLoggedIn = () => {
  return !!getToken();
};

function headersWithAuth(extra?: HeadersInit): Headers {
  const headers = new Headers(extra);
  const token = getToken();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return headers;
}

/**
 * Fetch with credentials + silent refresh:
 * - Proactively renews near-expired / expired access JWT before the request
 * - On UNAUTHORIZED after sending Bearer auth, refreshes once and retries
 */
const portalFetch: typeof fetch = async (input, init) => {
  const existing = getToken();
  if (existing && !isAccessTokenFresh(existing, 5_000)) {
    try {
      await ensureAccessViaRefresh();
    } catch {
      /* proceed; may still get 401 and hard-fail */
    }
  }

  const withCreds: RequestInit = {
    ...init,
    credentials: "include",
    headers: headersWithAuth(init?.headers),
  };

  const sentAuth =
    Boolean(getToken()) &&
    Boolean(withCreds.headers && new Headers(withCreds.headers).get("Authorization"));

  const res = await fetch(input, withCreds);

  // Login / public failures without Bearer must not trigger refresh.
  if (!sentAuth) {
    return res;
  }

  const bodyText = await res.text();
  if (!isTrpcUnauthorizedResponse(res, bodyText)) {
    return new Response(bodyText, {
      status: res.status,
      statusText: res.statusText,
      headers: res.headers,
    });
  }

  try {
    await ensureAccessViaRefresh();
  } catch {
    return new Response(bodyText, {
      status: res.status,
      statusText: res.statusText,
      headers: res.headers,
    });
  }

  const retryInit: RequestInit = {
    ...init,
    credentials: "include",
    headers: headersWithAuth(init?.headers),
  };
  return fetch(input, retryInit);
};

// Single link so every portal role (admin, facilitator, expert, client, …) uses
// the same credentials + silent-refresh interceptor.
const trpc = createTRPCClient<PortalRouter>({
  links: [
    httpLink({
      url: `${API_URL}/trpc`,
      fetch: portalFetch,
      transformer: superjson,
    }),
  ],
});

export default trpc;
